/**
 * IEC 61131-3 Structured Text (ST) Language Syntax Highlighter
 * 
 * Provides comprehensive token-based highlighting with context awareness
 * Supports full IEC 61131-3 standard features including:
 * - Variable declarations (VAR, VAR_INPUT, VAR_OUTPUT, etc.)
 * - Attribute qualifiers (AT, RETAIN, PERSISTENT, CONSTANT)
 * - Advanced types (ARRAY, STRING[n], POINTER TO)
 * - Industrial signal addresses (%IX, %QW, etc.)
 * - Time and date literals with IEC format
 */

export enum STTokenType {
  COMMENT = 'comment',
  PRAGMA = 'pragma',
  STRING = 'string',
  LITERAL_TIME = 'literal_time',
  LITERAL_DATE = 'literal_date',
  NUMBER_HEX = 'number_hex',
  NUMBER_BINARY = 'number_binary',
  NUMBER_OCTAL = 'number_octal',
  NUMBER_FLOAT = 'number_float',
  NUMBER = 'number',
  ADDRESS = 'address',
  KEYWORD_CONTROL = 'keyword_control',
  KEYWORD_VAR = 'keyword_var',
  KEYWORD_STRUCT = 'keyword_struct',
  KEYWORD_REGION = 'keyword_region',
  KEYWORD_FUNCTION = 'keyword_function',
  TYPE_STANDARD = 'type_standard',
  TYPE_ADVANCED = 'type_advanced',
  ATTRIBUTE_QUALIFIER = 'attribute_qualifier',
  FUNCTION_CALL = 'function_call',
  IDENTIFIER = 'identifier',
  OPERATOR = 'operator',
  BOOLEAN_LITERAL = 'boolean_literal',
}

/**
 * IEC 61131-3 Standard Keywords
 */
const IEC_KEYWORDS = {
  // Control flow
  control: new Set([
    'IF', 'THEN', 'ELSE', 'ELSIF', 'END_IF',
    'CASE', 'OF', 'END_CASE',
    'FOR', 'TO', 'BY', 'DO', 'END_FOR',
    'WHILE', 'END_WHILE',
    'REPEAT', 'UNTIL', 'END_REPEAT',
    'RETURN', 'EXIT'
  ]),

  // Variable declarations
  var: new Set([
    'VAR', 'VAR_INPUT', 'VAR_OUTPUT', 'VAR_IN_OUT',
    'VAR_GLOBAL', 'VAR_TEMP', 'VAR_STAT', 'VAR_EXTERNAL',
    'END_VAR'
  ]),

  // Structure definitions
  struct: new Set([
    'PROGRAM', 'END_PROGRAM',
    'FUNCTION', 'END_FUNCTION',
    'FUNCTION_BLOCK', 'END_FUNCTION_BLOCK',
    'ORGANIZATION_BLOCK', 'END_ORGANIZATION_BLOCK',
    'DATA_BLOCK', 'END_DATA_BLOCK',
    'TYPE', 'END_TYPE',
    'STRUCT', 'END_STRUCT',
    'ARRAY', 'END_ARRAY'
  ]),

  // Logical operators
  operator: new Set([
    'AND', 'OR', 'XOR', 'NOT', 'MOD'
  ]),

  // Attribute qualifiers (must be separated from regular keywords)
  qualifier: new Set([
    'AT', 'CONSTANT', 'RETAIN', 'PERSISTENT', 'NON_RETAIN',
    'S5TIME'
  ]),

  // Region markers (Siemens SCL)
  region: new Set([
    'REGION', 'END_REGION'
  ])
};

/**
 * IEC 61131-3 Standard Data Types
 */
const IEC_TYPES = {
  // Basic types
  basic: new Set([
    'BOOL', 'BYTE', 'WORD', 'DWORD', 'LWORD',
    'INT', 'UINT', 'DINT', 'UDINT', 'LINT', 'ULINT',
    'REAL', 'LREAL', 'CHAR', 'WCHAR',
    'TIME', 'LTIME', 'DATE', 'TIME_OF_DAY', 'DATE_AND_TIME',
    'STRING', 'WSTRING'
  ]),

  // Advanced types (qualified with ARRAY, POINTER, etc.)
  advanced: new Set([
    'POINTER', 'REFERENCE', 'ARRAY', 'STRUCT'
  ])
};

interface TokenMatch {
  type: STTokenType;
  value: string;
  index: number;
  length: number;
}

/**
 * Build comprehensive token regex patterns
 * Order matters: more specific patterns should come first
 */
function buildTokenPatterns(): [RegExp, (match: string, groups: Record<string, string>) => STTokenType | null][] {
  const patterns: [RegExp, (match: string, groups: Record<string, string>) => STTokenType | null][] = [];

  // ============ COMMENTS ============
  // Line comments: //
  patterns.push([
    /\/\/.*?(?=\n|$)/gi,
    () => STTokenType.COMMENT
  ]);

  // Block comments: (* ... *) and /* ... */
  // Supports basic nesting for IEC compliance
  patterns.push([
    /\(\*[\s\S]*?\*\)|\/\*[\s\S]*?\*\//g,
    () => STTokenType.COMMENT
  ]);

  // ============ PRAGMAS ============
  patterns.push([
    /\{[^}]*\}/g,
    () => STTokenType.PRAGMA
  ]);

  // ============ STRINGS ============
  patterns.push([
    /"[^"]*"|'[^']*'/g,
    () => STTokenType.STRING
  ]);

  // ============ TIME AND DATE LITERALS ============
  // TIME literals: T#1d2h3m4s5ms, T#1.5s, etc.
  patterns.push([
    /\b(T|TIME|LTIME)#[0-9.,_\-dmshABCEFabcef]+/gi,
    () => STTokenType.LITERAL_TIME
  ]);

  // DATE literals: D#2024-01-15, DT#2024-01-15-10:30:00, etc.
  patterns.push([
    /\b(D|DATE|DT|DATE_AND_TIME)#[\d\-.:_/]+/gi,
    () => STTokenType.LITERAL_DATE
  ]);

  // ============ INDUSTRIAL ADDRESSES (BEFORE NUMBERS) ============
  // %IX0.0, %QW10, %M3.5, etc. - Match entire address as single token
  // MUST be before NUMBER patterns to prevent partial matching
  patterns.push([
    /%[IQM][XBWDL]?(?:[\d]+(?:\.[\d]+)?)?/gi,
    () => STTokenType.ADDRESS
  ]);

  // ============ NUMBER LITERALS ============
  // Hexadecimal: 16#ABCD, 2#1010, 8#755
  patterns.push([
    /\b16#[0-9a-fA-F_]+\b/g,
    () => STTokenType.NUMBER_HEX
  ]);

  patterns.push([
    /\b2#[01_]+\b/g,
    () => STTokenType.NUMBER_BINARY
  ]);

  patterns.push([
    /\b8#[0-7_]+\b/g,
    () => STTokenType.NUMBER_OCTAL
  ]);

  // Floating point: 3.14, 1.5e-2, .5
  patterns.push([
    /\b\d+\.\d+(?:[eE][+-]?\d+)?\b|\b\.\d+(?:[eE][+-]?\d+)?\b/g,
    () => STTokenType.NUMBER_FLOAT
  ]);

  // Integer
  patterns.push([
    /\b\d+\b/g,
    () => STTokenType.NUMBER
  ]);

  // ============ BOOLEAN LITERALS ============
  patterns.push([
    /\b(TRUE|FALSE)\b/gi,
    () => STTokenType.BOOLEAN_LITERAL
  ]);

  // ============ KEYWORDS (Case-insensitive) ============
  // Qualifiers: AT, CONSTANT, RETAIN, etc.
  patterns.push([
    /\b(AT|CONSTANT|RETAIN|PERSISTENT|NON_RETAIN|S5TIME)\b/gi,
    (match) => IEC_KEYWORDS.qualifier.has(match.toUpperCase()) ? STTokenType.ATTRIBUTE_QUALIFIER : null
  ]);

  // Variable Keywords (VAR, VAR_INPUT, etc.)
  patterns.push([
    /\b(VAR|VAR_INPUT|VAR_OUTPUT|VAR_IN_OUT|VAR_GLOBAL|VAR_TEMP|VAR_STAT|VAR_EXTERNAL|END_VAR)\b/gi,
    (match) => IEC_KEYWORDS.var.has(match.toUpperCase()) ? STTokenType.KEYWORD_VAR : null
  ]);

  // Structure Keywords (PROGRAM, FUNCTION, etc.)
  patterns.push([
    /\b(PROGRAM|END_PROGRAM|FUNCTION|END_FUNCTION|FUNCTION_BLOCK|END_FUNCTION_BLOCK|ORGANIZATION_BLOCK|END_ORGANIZATION_BLOCK|DATA_BLOCK|END_DATA_BLOCK|TYPE|END_TYPE|STRUCT|END_STRUCT|ARRAY|END_ARRAY)\b/gi,
    (match) => IEC_KEYWORDS.struct.has(match.toUpperCase()) ? STTokenType.KEYWORD_STRUCT : null
  ]);

  // Region markers (REGION, END_REGION) - Siemens SCL
  patterns.push([
    /\b(REGION|END_REGION)\b/gi,
    (match) => IEC_KEYWORDS.region.has(match.toUpperCase()) ? STTokenType.KEYWORD_REGION : null
  ]);

  // Control flow keywords (IF, THEN, WHILE, etc.)
  patterns.push([
    /\b(IF|THEN|ELSE|ELSIF|END_IF|CASE|OF|END_CASE|FOR|TO|BY|DO|END_FOR|WHILE|END_WHILE|REPEAT|UNTIL|END_REPEAT|RETURN|EXIT)\b/gi,
    (match) => IEC_KEYWORDS.control.has(match.toUpperCase()) ? STTokenType.KEYWORD_CONTROL : null
  ]);

  // Logical operators (AND, OR, NOT, XOR, MOD)
  patterns.push([
    /\b(AND|OR|XOR|NOT|MOD)\b/gi,
    (match) => IEC_KEYWORDS.operator.has(match.toUpperCase()) ? STTokenType.KEYWORD_CONTROL : null
  ]);

  // ============ TYPES ============
  // Standard types (BOOL, INT, REAL, STRING, etc.)
  patterns.push([
    /\b(BOOL|BYTE|WORD|DWORD|LWORD|INT|UINT|DINT|UDINT|LINT|ULINT|REAL|LREAL|CHAR|WCHAR|TIME|LTIME|DATE|TIME_OF_DAY|DATE_AND_TIME|STRING|WSTRING)\b(?:\s*\[)?/gi,
    (match) => IEC_TYPES.basic.has(match.toUpperCase().split(/[\s\[]/)[0]) ? STTokenType.TYPE_STANDARD : null
  ]);

  // Advanced types (ARRAY, POINTER, etc.)
  patterns.push([
    /\b(ARRAY|POINTER|REFERENCE)\b/gi,
    (match) => IEC_TYPES.advanced.has(match.toUpperCase()) ? STTokenType.TYPE_ADVANCED : null
  ]);

  // ============ FUNCTION CALLS ============
  // Function names followed by opening parenthesis
  patterns.push([
    /\b[a-zA-Z_]\w*(?=\s*\()/g,
    (match) => {
      const upper = match.toUpperCase();
      // Exclude keywords - they're already handled
      if (IEC_KEYWORDS.control.has(upper) ||
          IEC_KEYWORDS.var.has(upper) ||
          IEC_KEYWORDS.struct.has(upper) ||
          IEC_KEYWORDS.region.has(upper) ||
          IEC_KEYWORDS.operator.has(upper)) {
        return null;
      }
      return STTokenType.FUNCTION_CALL;
    }
  ]);

  // ============ OPERATORS ============
  // Assignment, comparison, arithmetic, logical
  patterns.push([
    /:=|=>|<=|>=|<>|\.\.|\*\*|\+|-|\*|\/|=|<|>|;|:|#|&|\||~|\^/g,
    () => STTokenType.OPERATOR
  ]);

  // ============ IDENTIFIERS ============
  // User-defined names (variables, custom types)
  // Including dot notation (e.g., obj.field) and Siemens local vars (#var)
  patterns.push([
    /(?:\b|#)[a-zA-Z_]\w*(?:\.\w+)*/g,
    (match) => {
      const upper = match.toUpperCase();
      // Exclude keywords
      if (IEC_KEYWORDS.control.has(upper) ||
          IEC_KEYWORDS.var.has(upper) ||
          IEC_KEYWORDS.struct.has(upper) ||
          IEC_KEYWORDS.region.has(upper) ||
          IEC_KEYWORDS.operator.has(upper) ||
          IEC_KEYWORDS.qualifier.has(upper) ||
          IEC_TYPES.basic.has(upper) ||
          IEC_TYPES.advanced.has(upper)) {
        return null;
      }
      return STTokenType.IDENTIFIER;
    }
  ]);

  return patterns;
}

/**
 * Tokenize ST code with comprehensive pattern matching
 */
export function tokenizeST(code: string): TokenMatch[] {
  const tokens: TokenMatch[] = [];
  const processed = new Set<number>();

  const patterns = buildTokenPatterns();

  for (const [regex, typeResolver] of patterns) {
    let match;
    regex.lastIndex = 0;
    
    while ((match = regex.exec(code)) !== null) {
      const startIndex = match.index;
      const endIndex = startIndex + match[0].length;

      // Skip if this position was already covered by higher-priority pattern
      let skip = false;
      for (let i = startIndex; i < endIndex; i++) {
        if (processed.has(i)) {
          skip = true;
          break;
        }
      }

      if (!skip) {
        const tokenType = typeResolver(match[0], {});
        if (tokenType) {
          tokens.push({
            type: tokenType,
            value: match[0],
            index: startIndex,
            length: match[0].length
          });

          // Mark these positions as processed
          for (let i = startIndex; i < endIndex; i++) {
            processed.add(i);
          }
        }
      }
    }
  }

  // Sort by index for proper ordering
  tokens.sort((a, b) => a.index - b.index);

  // Merge adjacent tokens of same type (optional - helps reduce span count)
  return tokens;
}

/**
 * HTML escape helper
 */
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * Convert token type to CSS class name and color
 */
interface TokenStyle {
  className: string;
  color: string;
}

function tokenTypeToStyle(type: STTokenType): TokenStyle {
  const mapping: Record<STTokenType, TokenStyle> = {
    [STTokenType.COMMENT]: { className: 'plc-comment', color: '#6a9955' },
    [STTokenType.PRAGMA]: { className: 'plc-pragma', color: '#d19a66' },
    [STTokenType.STRING]: { className: 'plc-string', color: '#ce9178' },
    [STTokenType.LITERAL_TIME]: { className: 'plc-literal', color: '#d19a66' },
    [STTokenType.LITERAL_DATE]: { className: 'plc-literal', color: '#d19a66' },
    [STTokenType.NUMBER_HEX]: { className: 'plc-literal', color: '#d19a66' },
    [STTokenType.NUMBER_BINARY]: { className: 'plc-literal', color: '#d19a66' },
    [STTokenType.NUMBER_OCTAL]: { className: 'plc-literal', color: '#d19a66' },
    [STTokenType.NUMBER_FLOAT]: { className: 'plc-literal', color: '#d19a66' },
    [STTokenType.NUMBER]: { className: 'plc-literal', color: '#d19a66' },
    [STTokenType.ADDRESS]: { className: 'plc-address', color: '#d946ef' },
    [STTokenType.KEYWORD_CONTROL]: { className: 'plc-keyword', color: '#569cd6' },
    [STTokenType.KEYWORD_VAR]: { className: 'plc-var-keyword', color: '#569cd6' },
    [STTokenType.KEYWORD_STRUCT]: { className: 'plc-struct-keyword', color: '#4ec9b0' },
    [STTokenType.KEYWORD_REGION]: { className: 'plc-region-keyword', color: '#f06292' },
    [STTokenType.KEYWORD_FUNCTION]: { className: 'plc-keyword', color: '#569cd6' },
    [STTokenType.ATTRIBUTE_QUALIFIER]: { className: 'plc-attribute', color: '#d19a66' },
    [STTokenType.TYPE_STANDARD]: { className: 'plc-type', color: '#4ec9b0' },
    [STTokenType.TYPE_ADVANCED]: { className: 'plc-type-advanced', color: '#4ec9b0' },
    [STTokenType.FUNCTION_CALL]: { className: 'plc-function', color: '#dcdcaa' },
    [STTokenType.IDENTIFIER]: { className: 'plc-identifier', color: '#9cdcfe' },
    [STTokenType.OPERATOR]: { className: 'plc-operator', color: '#d4d4d4' },
    [STTokenType.BOOLEAN_LITERAL]: { className: 'plc-boolean', color: '#ffca28' },
  };
  return mapping[type] || { className: 'plc-default', color: '#d4d4d4' };
}

export function highlightSTCode(code: string): string {
  // Tokenize the RAW code first
  const tokens = tokenizeST(code);

  if (tokens.length === 0) {
    // No tokens found - return escaped text as fallback
    return escapeHtml(code);
  }

  let html = '';
  let lastIndex = 0;

  for (const token of tokens) {
    // Add (and escape) unmatched text before this token
    if (token.index > lastIndex) {
      html += escapeHtml(code.substring(lastIndex, token.index));
    }

    // Add highlighted token while escaping its value
    const style = tokenTypeToStyle(token.type);
    const escapedValue = escapeHtml(token.value);
    html += `<span class="${style.className}" style="color: ${style.color};">${escapedValue}</span>`;

    lastIndex = token.index + token.length;
  }

  // Add (and escape) remaining text
  if (lastIndex < code.length) {
    html += escapeHtml(code.substring(lastIndex));
  }

  return html;
}

/**
 * Text already known to be comment: a line inside a block comment that spans
 * several lines, which the tokenizer, reading one line at a time, cannot see.
 */
export function highlightSTComment(text: string): string {
  if (!text) return '';
  const style = tokenTypeToStyle(STTokenType.COMMENT);
  return `<span class="${style.className}" style="color: ${style.color};">${escapeHtml(text)}</span>`;
}

/**
 * Export token type utility for testing and debugging
 */
export function getTokenType(text: string): STTokenType | null {
  const tokens = tokenizeST(text);
  return tokens.length > 0 ? tokens[0].type : null;
}
